import { Config } from '../config/config.js';
import { isUserLoggedIn } from '../utils/is-logged-in.js';
import { resolveFetch as resolveFormFetch, submitFormRequest } from './form-submit.js';

const sharedConfig = new Config();

const TEAM_TYPE_LABELS = {
  scrum: 'Scrum',
  kanban: 'Kanban',
  safe: 'SAFe',
  less: 'LeSS',
  xp: 'XP',
  agile: 'Agile',
};

const TEAM_TYPE_SLUG_LOOKUP = Object.keys(TEAM_TYPE_LABELS).reduce((acc, slug) => {
  acc[slug] = slug;
  acc[TEAM_TYPE_LABELS[slug].toLowerCase()] = slug;
  return acc;
}, Object.create(null));

const TEAM_TOOL_CANONICAL_LABELS = {
  jira: 'Jira',
  'azure-devops': 'Azure DevOps',
  'version-one': 'Agility (VersionOne)',
  rally: 'Rally',
};

const TEAM_TOOL_LABELS = {
  ...TEAM_TOOL_CANONICAL_LABELS,
  'azure devops': 'Azure DevOps',
  'agility (versionone)': 'Agility (VersionOne)',
};

const TEAM_TOOL_LABEL_TO_SLUG = Object.entries(TEAM_TOOL_CANONICAL_LABELS).reduce((acc, [slug, label]) => {
  acc[label.toLowerCase()] = slug;
  return acc;
}, Object.create(null));

function normaliseApiBase(value) {
  if (typeof value === 'string' && value.length) {
    return value.endsWith('/') ? value.slice(0, -1) : value;
  }
  return sharedConfig.apiBase.replace(/\/$/, '');
}

function isApiModule(candidate) {
  if (!candidate || typeof candidate !== 'object') {
    return false;
  }
  return (
    typeof candidate.listTeams === 'function' ||
    typeof candidate.createTeam === 'function' ||
    typeof candidate.uploadTeamLogo === 'function' ||
    typeof candidate.getTeamDetails === 'function'
  );
}

function buildTeamRequestPayload(input, projectId) {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const { name, projectId: nestedProjectId, ...rest } = input;
    const trimmedName = typeof name === 'string' ? name.trim() : name;
    if (!trimmedName) {
      throw new Error('Team name is required');
    }
    const payload = { ...rest, name: trimmedName };
    const effectiveProjectId = nestedProjectId ?? projectId ?? null;
    if (effectiveProjectId) {
      payload.projectId = effectiveProjectId;
    }

    const typeInfo = coerceTeamType(rest.type ?? rest.teamType ?? rest.team_type ?? null);
    if (typeInfo) {
      payload.type = typeInfo.label;
      payload.teamType = typeInfo.slug;
      payload.team_type = typeInfo.label;
    } else {
      delete payload.type;
      delete payload.teamType;
      delete payload.team_type;
    }

    const toolInfo = coerceTeamTool(rest.tool ?? rest.pmTool ?? rest.pm_tool ?? null);
    if (toolInfo) {
      payload.tool = toolInfo.label;
      payload.pmTool = toolInfo.slug;
      payload.pm_tool = toolInfo.label;
    } else {
      delete payload.tool;
      delete payload.pmTool;
      delete payload.pm_tool;
    }

    const rawToken = rest.token ?? rest.apiToken ?? rest.api_token ?? null;
    if (rawToken != null) {
      const tokenValue = typeof rawToken === 'string' ? rawToken.trim() : rawToken;
      if (tokenValue) {
        payload.token = tokenValue;
        payload.apiToken = tokenValue;
        payload.api_token = tokenValue;
      } else {
        delete payload.token;
        delete payload.apiToken;
        delete payload.api_token;
      }
    }

    const rawBoardUrl = rest.board_url ?? rest.boardUrl ?? rest.boardURL ?? null;
    if (rawBoardUrl != null) {
      const boardUrlValue = typeof rawBoardUrl === 'string' ? rawBoardUrl.trim() : rawBoardUrl;
      if (boardUrlValue) {
        payload.board_url = boardUrlValue;
        payload.boardUrl = boardUrlValue;
      } else {
        delete payload.board_url;
        delete payload.boardUrl;
      }
    }

    cleanupLegacyTeamFields(payload);
    return payload;
  }

  const trimmedName = typeof input === 'string' ? input.trim() : input;
  if (!trimmedName) {
    throw new Error('Team name is required');
  }

  const payload = { name: trimmedName };
  if (projectId) {
    payload.projectId = projectId;
  }
  cleanupLegacyTeamFields(payload);
  return payload;
}

function cleanupLegacyTeamFields(payload) {
  if (!payload || typeof payload !== 'object') {
    return;
  }
  delete payload.boardURL;
}

function coerceTeamType(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const lower = trimmed.toLowerCase();
  const slug = TEAM_TYPE_SLUG_LOOKUP[lower] || lower;
  const label = TEAM_TYPE_LABELS[slug] || trimmed;
  return { label, slug };
}

function coerceTeamTool(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const lower = trimmed.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(TEAM_TOOL_CANONICAL_LABELS, lower)) {
    return { label: TEAM_TOOL_CANONICAL_LABELS[lower], slug: lower };
  }
  const slugFromLabel = TEAM_TOOL_LABEL_TO_SLUG[lower];
  if (slugFromLabel) {
    return { label: TEAM_TOOL_CANONICAL_LABELS[slugFromLabel], slug: slugFromLabel };
  }
  const aliasLabel = TEAM_TOOL_LABELS[lower];
  if (aliasLabel) {
    const canonicalSlug = TEAM_TOOL_LABEL_TO_SLUG[aliasLabel.toLowerCase()] || lower.replace(/\s+/g, '-');
    return { label: aliasLabel, slug: canonicalSlug };
  }
  return { label: trimmed, slug: lower.replace(/\s+/g, '-') };
}

export class TeamsGateway {
  constructor(apiOrOptions = null, maybeOptions = {}) {
    let apiCandidate = apiOrOptions;
    let options = maybeOptions;

    if (apiCandidate && !isApiModule(apiCandidate)) {
      options = apiCandidate || {};
      apiCandidate = null;
    }

    if (isApiModule(apiCandidate)) {
      this.api = apiCandidate;
      this.fetch = null;
      this.apiBase = null;
    } else {
      const { fetch: fetchImpl, apiBase } = options || {};
      this.api = null;
      this.fetch = resolveFormFetch(fetchImpl);
      this.apiBase = normaliseApiBase(apiBase);
    }
  }

  #resolveUrl(path) {
    const base = this.apiBase || normaliseApiBase();
    return `${base}${path}`;
  }

  #getFetch() {
    return this.fetch || resolveFormFetch();
  }

  async list(projectId = null) {
    if (!isUserLoggedIn()) {
      return [];
    }

    if (this.api) {
      if (typeof this.api.listTeams !== 'function') {
        return [];
      }
      try {
        const teams = await this.api.listTeams(projectId);
        return Array.isArray(teams) ? teams : [];
      } catch (error) {
        return [];
      }
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      return [];
    }

    const params = new URLSearchParams();
    if (projectId) {
      params.set('projectId', projectId);
    }

    try {
      const response = await fetch(this.#resolveUrl(`/api/data/teams${params.toString() ? `?${params.toString()}` : ''}`), {
        credentials: 'include',
      });
      if (!response || !response.ok) {
        return [];
      }
      const payload = await response.json();
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      return [];
    }
  }

  async create(teamData = {}, projectId = null) {
    if (this.api) {
      if (typeof this.api.createTeam !== 'function') {
        throw new Error('Teams API unavailable');
      }
      return this.api.createTeam(teamData, projectId);
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Teams gateway requires fetch');
    }

    const payload = buildTeamRequestPayload(teamData, projectId);

    const response = await fetch(this.#resolveUrl('/api/data/teams'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response || !response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || `Create team failed: ${response?.status ?? 'error'}`);
    }

    return response.json();
  }

  async uploadLogo(teamId, file) {
    if (this.api) {
      if (typeof this.api.uploadTeamLogo !== 'function') {
        throw new Error('Teams API unavailable');
      }
      return this.api.uploadTeamLogo(teamId, file);
    }

    if (!teamId) {
      throw new Error('Team ID and file are required');
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Teams gateway requires fetch');
    }

    const firstFile = Array.isArray(file) ? file[0] : (file && file.length !== undefined ? file[0] : file);
    if (!firstFile) {
      throw new Error('Team logo file is required');
    }

    await submitFormRequest({
      fetch,
      url: this.#resolveUrl(`/api/teams/${encodeURIComponent(teamId)}/logo`),
      files: [firstFile],
    });

    return undefined;
  }







  async fetchDetails(teamId) {
    if (!teamId) {
      return null;
    }

    if (this.api) {
      if (typeof this.api.getTeamDetails !== 'function') {
        return null;
      }
      try {
        return await this.api.getTeamDetails(teamId);
      } catch (error) {
        return null;
      }
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      return null;
    }

    try {
      const response = await fetch(this.#resolveUrl(`/api/data/teams/${encodeURIComponent(teamId)}`), {
        credentials: 'include',
      });
      if (!response || !response.ok) {
        return null;
      }
      return response.json();
    } catch (error) {
      return null;
    }
  }
}

if (typeof window !== 'undefined') {
  window.AgileProGateways = window.AgileProGateways || {};
  window.AgileProGateways.TeamsGateway = TeamsGateway;
}
